import type { VesselMessage } from '../../../../types/VesselMessage';
import { Button } from '../../../cdl/Button/Button';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { TextButton } from '../../../common/buttons/TextButton';
import { GenericError } from '../../../common/Error/GenericError';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { useVesselBudgetDetails } from '../../../common/hooks/useVesselBudgetDetails';
import { IconCoin } from '../../../common/icons/cdl/Coin';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Section } from '../../../common/Section/Section';
import { TableEmptyState } from '../../../common/TableEmptyState/TableEmptyState';
import { Box } from '../../../common/ui/Box';

import type { VesselBudgetFormValues } from './BudgetForm';
import { BudgetForm } from './BudgetForm';
import { BudgetInformation } from './BudgetInformation';
import { createVesselBudgetRequest } from './createVesselBudgetRequest';
import { DeleteBudgetPopupButton } from './DeleteBudgetPopupButton';
import { updateVesselBudgetRequest } from './updateVesselBudgetRequest';
import { useVesselBudgetCreate } from './useVesselBudgetCreate';
import { useVesselBudgetDelete } from './useVesselBudgetDelete';
import { useVesselBudgetEdit } from './useVesselBudgetEdit';

interface VesselBudgetProps {
    vessel: VesselMessage;
}

export const VesselBudget = ({ vessel }: VesselBudgetProps) => {
    const vesselId = vessel.id;
    const role = useRole();

    const { isPending, isError, budgetData, notFound } = useVesselBudgetDetails(vesselId);

    const { mutateAsync: createBudget } = useVesselBudgetCreate(vesselId);
    const { mutateAsync: updateBudget } = useVesselBudgetEdit(vesselId);
    const { mutate: deleteBudget } = useVesselBudgetDelete(vesselId);

    const { push, pop } = useDrawer();

    const onCreateBudget = () => {
        const onCreateSubmit = async (values: VesselBudgetFormValues) => {
            try {
                await createBudget(createVesselBudgetRequest(values, vessel));
                pop(drawerId);
            } catch {
                return;
            }
        };

        const drawerId = push({
            header: { title: translate('budgets.createBudget') },
            content: (
                <BudgetForm
                    primaryActionText={translate('budgets.createBudget')}
                    onSubmit={onCreateSubmit}
                    budgetData={budgetData}
                />
            ),
        });
    };

    const onEditBudget = () => {
        const onEditSubmit = async (values: VesselBudgetFormValues) => {
            if (!budgetData) {
                return;
            }

            try {
                await updateBudget(updateVesselBudgetRequest(values, budgetData));
                pop(drawerId);
            } catch {
                return;
            }
        };

        const drawerId = push({
            header: { title: translate('budgets.editBudget') },
            content: (
                <BudgetForm
                    primaryActionText={translate('budgets.updateBudget')}
                    onSubmit={onEditSubmit}
                    budgetData={budgetData}
                />
            ),
        });
    };

    if (isPending) {
        return (
            <Section title={translate('budgets.title')}>
                <LoadingIndicator />
            </Section>
        );
    }

    if (notFound) {
        return (
            <Section
                title={translate('budgets.title')}
                actions={
                    <TextButton onClick={onCreateBudget}>
                        <IconPlus width={16} height={16} />
                        {translate('budgets.createBudget')}
                    </TextButton>
                }
            >
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <TableEmptyState text={translate('budgets.emptyState')} Icon={IconCoin} />
                </Box>
            </Section>
        );
    }

    if (isError) {
        return (
            <Section title={translate('budgets.title')}>
                <GenericError />
            </Section>
        );
    }

    if (!budgetData) {
        return (
            <Section title={translate('budgets.title')}>
                <GenericError />
            </Section>
        );
    }

    return (
        <Section
            title={translate('budgets.title')}
            actions={
                role.hasWriteRights(vessel.customerId) ? (
                    <Box display="flex" flexDirection="row" columnGap={8}>
                        <Button onClick={onEditBudget}>{translate('actions.edit')}</Button>
                        <DeleteBudgetPopupButton onConfirm={() => deleteBudget(budgetData.id)} />
                    </Box>
                ) : null
            }
            fullHeight={false}
            paddingY={0}
        >
            <Box paddingX={32} paddingY={24}>
                <BudgetInformation budgetData={budgetData} />
            </Box>
        </Section>
    );
};
